import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AfasEmployeeTypePipe } from 'src/app/pipes/afas-employee-type.pipe';
import { environment } from 'src/environments/environment';
import { User } from '../../classes/user.class';
import { Utils } from '../../utils.class';
import { ApiResponse, ApiService } from '../api/api.service';
import { AuthorisationService } from '../auth/authorisation.service';
import { Functionalities } from '../auth/functionalities';
import { RealtimeType } from '../realtime/realtime-type.enum';
import { RealtimeService } from '../realtime/realtime.service';
import { WebsocketService } from '../websocket/websocket.service';
import { UserSettings, UserSettingsModuleKey, UserSettingsModuleMap } from './user-settings';

@Injectable()
export class UserService extends RealtimeService<User> {
    listOnlyFilteredFrontend = true;
    environment = environment;

    constructor(
        protected apiService: ApiService,
        protected websocketService: WebsocketService
    ) {
        super(websocketService, RealtimeType.users);
    }

    public itemInDaterange(user: User, fromDate: Date, toDate: Date) {
        const fromTime = fromDate.getTime();
        return !user.enddate || Utils.getTimeOrNull(user.enddate) >= fromTime;
    }

    userFunctions(officeWorkers = true) {
        const params = officeWorkers ? { plannable: false, external: false } : {};
        return this.apiService.getCall$<string[]>(`${this.type}/functions`, params);
    }

    public saveUser(user: User): Promise<User> {
        return this.apiService.postCall(this.type, user);
    }

    /**
     * deprecated
     * @param role
     */
    public getByType(role: string | string[]): Observable<User[]> {
        return this.getList().pipe(
            map(users => {
                if (Array.isArray(role)) {
                    return users.filter(user => user.roles.find(r => role.includes(r.key)));
                } else if (role) {
                    return users.filter(user => user.roles.find(r => r.key === role));
                } else {
                    return users;
                }
            })
        );
    }

    public getFilteredByType(fromDate: Date, toDate: Date, role: string): Observable<User[]> {
        return this.getFilteredList(fromDate, toDate).pipe(
            map(users => users.filter(user => user.roles.find(g => g.key === role)))
        );
    }

    public getFilteredList(
        fromDate: Date,
        toDate: Date,
        withTrashed = false,
        officeWorkers = true
    ): Observable<User[]> {
        return super
            .getFilteredList(fromDate, toDate)
            .pipe(map(users => this.filterTrashedOfficeWorkers(users, withTrashed, officeWorkers)));
    }

    private filterTrashedOfficeWorkers(
        users: User[],
        withTrashed = false,
        officeWorkers = true,
        withZzp = true,
        withFormerEmployees = false
    ): User[] {
        return (users || [])
            .filter(user => withTrashed || user.deleted_at === null)
            .filter(user => withZzp || !new AfasEmployeeTypePipe().transform(user.afas_employee_id, 'Z'))
            .filter(user => {
                const isPlannable = AuthorisationService.hasFunctionality(Functionalities.Plannable, user);
                const isExternal = AuthorisationService.hasFunctionality(Functionalities.External, user);
                const isFormerEmployee = AuthorisationService.hasRole(this.environment.roles.FormerEmployee, user);

                // Office workers are users without Plannable and without External functionality.
                // When officeWorkers is true, keep everyone.
                // When officeWorkers is false, keep only internal plannable employees.
                return officeWorkers || (isPlannable && !isExternal) || (withFormerEmployees && isFormerEmployee);
            });
    }

    public getList(
        withTrashed = false,
        officeWorkers = true,
        withZzp = true,
        withFormerEmployees = false
    ): Observable<User[]> {
        return super
            .getList()
            .pipe(
                map(users =>
                    this.filterTrashedOfficeWorkers(users, withTrashed, officeWorkers, withZzp, withFormerEmployees)
                )
            );
    }

    public getApiList(
        withTrashed = false,
        officeWorkers = true,
        withZzp = true,
        withFormerEmployees = false,
        includeExternal = false
    ): Observable<User[]> {
        return this.apiService
            .getCallData$<User[]>(`${this.type}`, { with_trashed: withTrashed, include_external: includeExternal })
            .pipe(
                map(users =>
                    this.filterTrashedOfficeWorkers(users, withTrashed, officeWorkers, withZzp, withFormerEmployees)
                )
            );
    }

    public getSingle(id: number | string): Observable<User> {
        return this.apiService.getCallData$<User[]>(`${this.type}/${id}`).pipe(map(users => users[0]));
    }

    public getPaginatedList(
        page: number,
        pageSize: number,
        showDeleted: boolean,
        search: string,
        roleKeys: string[],
        includeUsersWithoutRoles = false
    ): Observable<{ users: User[]; total: number }> {
        return this.apiService.getCallData$<{ users: User[]; total: number }>(`${this.type}/paginated`, {
            page,
            per_page: pageSize,
            with_trashed: showDeleted,
            search,
            role_keys: roleKeys.join(','),
            include_users_without_roles: includeUsersWithoutRoles,
        });
    }

    public getMap(withTrashed = false): Observable<Map<number, User>> {
        return this.getList(withTrashed).pipe(map(users => this.genEntitiesMap(users)));
    }

    public deleteUser(userId: number): Observable<object> {
        return this.apiService.deleteCall$<object>(`${this.type}/${userId}`);
    }

    public getUserRoleHistory(userId: number): Observable<UserRoleHistory[]> {
        return this.apiService.getCallData$<UserRoleHistory[]>(`${this.type}/role-history/${userId}`);
    }

    public getUserCustomerHistory(userId: number): Observable<UserCustomerHistory[]> {
        return this.apiService.getCallData$<UserCustomerHistory[]>(`${this.type}/customer-history/${userId}`);
    }

    public saveUserSettings<TKey extends UserSettingsModuleKey>(
        moduleKey: TKey,
        settings: UserSettingsModuleMap[TKey]
    ): Observable<ApiResponse<UserSettings>> {
        return this.apiService.patchCall$<UserSettings>(`auth/me/settings/${moduleKey}`, { settings });
    }

    public saveProfilePicture(profilePicture: string): Observable<User> {
        return this.apiService.postCallObserver<User>('auth/me/profile-picture', { profile_picture: profilePicture });
    }

    private genEntitiesMap(usersList: User[]): Map<number, User> {
        const map = new Map<number, User>();
        usersList.forEach(user => {
            map.set(user.id, user);
        });
        return map;
    }
}

export interface UserRoleHistory {
    role_key: string;
    role_name: string;
    role_description: string;
    assigned_at: Date;
    updated_at: Date;
    removed_at?: Date;
    assigned_by_id?: number;
    assigned_by_name: string;
    deleted_by_id?: number;
    deleted_by_name?: string;
    is_active: boolean;
    status: 'Active' | 'Removed';
    default: boolean;
    restricted: boolean;
}

export interface UserCustomerHistory {
    customer_id: number;
    customer_name: string;
    assigned_at: Date;
    updated_at: Date;
    removed_at?: Date;
    assigned_by_id?: number;
    assigned_by_name: string;
    deleted_by_id?: number;
    deleted_by_name?: string;
    is_active: boolean;
    status: 'Active' | 'Removed';
}
